import moment from 'moment';

/**
 * Frontend concept metadata layer: mirrors utils/conceptBehaviors.js on the
 * backend so the debt/credit editors render and validate by behavior instead
 * of by hardcoded slug. Keep the legacy maps in sync with the backend
 * LEGACY_DEBT_BEHAVIORS / LEGACY_CREDIT_BEHAVIORS.
 */

// Shared concept name -> slug normalizer (same logic as the backend
// normalizeConceptKey and the editors' historical normalizeConceptName).
export const normalizeConceptName = (name) => {
	return String(name)
		.toLowerCase()
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '')
		.replace(/\s+/g, '-');
};

// Legacy slug -> metadata. Both accented and unaccented forms of
// 'prestamos-interés' are mapped because production data may contain either.
export const FRONTEND_LEGACY_DEBT_META = {
	'alquileres': { behavior: 'periodic', requiresContract: true, ipcAdjustable: true, commissionable: true },
	'garantia-de-alquiler': { behavior: 'deposit', requiresContract: true },
	'gasto-de-activo-fijo': { behavior: 'periodic', requiresContract: true, treatAsExpense: true },
	'prestamos-interés': { behavior: 'interest-only' },
	'prestamos-interes': { behavior: 'interest-only' },
	'prestamos-amortizable': { behavior: 'amortizable' },
	'ventas-a-plazo': { behavior: 'installment-sale', requiresAssetSale: true, commissionable: true },
};

export const FRONTEND_LEGACY_CREDIT_META = {
	'alquileres': { behavior: 'periodic', requiresContract: true },
	'prestamos-interés': { behavior: 'interest-only' },
	'prestamos-interes': { behavior: 'interest-only' },
	'prestamos-amortizable': { behavior: 'amortizable' },
	'entrega-cuenta': { behavior: 'account-delivery' },
};

// Display names for the fetch-failure fallback path of the editors. Only the
// slugs present here are offered when the concept list cannot be loaded
// (the unaccented 'prestamos-interes' duplicate is intentionally excluded).
export const FALLBACK_DEBT_CONCEPT_NAMES = {
	'prestamos-amortizable': 'Préstamos amortizable',
	'prestamos-interés': 'Préstamos interés',
	'alquileres': 'Alquileres',
	'garantia-de-alquiler': 'Garantía de alquiler',
	'ventas-a-plazo': 'Ventas a plazo',
	'gasto-de-activo-fijo': 'Gasto de activo fijo',
};

export const FALLBACK_CREDIT_CONCEPT_NAMES = {
	'prestamos-amortizable': 'Préstamos amortizable',
	'prestamos-interés': 'Préstamos interés',
	'entrega-cuenta': 'Entrega a cuenta',
};

/**
 * Build a map key -> metadata from fetched concept docs. Docs without a key
 * are indexed by their normalized name (admin-created docs predating the key
 * field). Docs without a behavior are skipped so getMeta() falls through to
 * the legacy map, matching the backend resolveConceptMeta() order.
 */
export const buildConceptMeta = (conceptDocs) => {
	const map = {};
	for (const doc of conceptDocs || []) {
		if (!doc || (!doc.key && !doc.name)) continue;
		const key = doc.key || normalizeConceptName(doc.name);
		if (!key || map[key] || !doc.behavior) continue;
		map[key] = {
			behavior: doc.behavior,
			requiresContract: !!doc.requiresContract,
			requiresAssetSale: !!doc.requiresAssetSale,
			allowsPerUser: !!doc.allowsPerUser,
			treatAsExpense: !!doc.treatAsExpense,
			defaultPeriodicity: doc.defaultPeriodicity,
			accrual: doc.accrual,
			arrearsDueDay: doc.arrearsDueDay,
		};
	}
	return map;
};

/** Metadata for a slug: concept doc map -> legacy map -> generic periodic. */
export const getMeta = (key, metaMap, legacyMap) => {
	if (key && metaMap && metaMap[key]) return metaMap[key];
	if (key && legacyMap && legacyMap[key]) return legacyMap[key];
	return { behavior: 'periodic' };
};

// Closed behavior vocabulary: the concept manager composes concepts FROM
// these — new billing math is always a code change, never configuration.
export const BEHAVIOR_OPTIONS = [
	{ value: 'periodic', labelKey: 'concept.behavior.periodic' },
	{ value: 'amortizable', labelKey: 'concept.behavior.amortizable' },
	{ value: 'interest-only', labelKey: 'concept.behavior.interestOnly' },
	{ value: 'installment-sale', labelKey: 'concept.behavior.installmentSale' },
	{ value: 'deposit', labelKey: 'concept.behavior.deposit' },
	{ value: 'account-delivery', labelKey: 'concept.behavior.accountDelivery' },
	{ value: 'one-off', labelKey: 'concept.behavior.oneOff' },
];

// When a period's due is generated and when it expires (backend: concept
// accrual, utils/dueStrategies.js).
export const ACCRUAL_OPTIONS = [
	{ value: 'advance', labelKey: 'concept.accrual.advance' },
	{ value: 'arrears', labelKey: 'concept.accrual.arrears' },
];

export const behaviorNeedsCapital = (behavior) =>
	['amortizable', 'installment-sale', 'interest-only', 'account-delivery'].includes(behavior);

export const behaviorNeedsDues = (behavior) =>
	['amortizable', 'installment-sale', 'account-delivery'].includes(behavior);

export const behaviorNeedsInterest = (behavior) =>
	['amortizable', 'installment-sale', 'interest-only'].includes(behavior);

// Deposits (garantia-de-alquiler) only require dues when collected in
// installments; a single 'efectivo' collection needs none.
export const depositNeedsDues = (behavior, periodicity) =>
	behavior === 'deposit' && periodicity !== 'efectivo';

// A blank/NaN check (rather than a truthiness check) so a legitimate 0 value
// — e.g. a zero-amount expense record, or 0% interest — is never mistaken
// for "not filled in". Interest additionally rejects negatives.
const isBlank = (value) => value === undefined || value === null || value === '' || isNaN(Number(value));

/**
 * Deterministic periodicity/dues for a freshly selected concept — used on a
 * concept change so a stale value from the previous concept can never survive
 * the switch (e.g. capital behavior inheriting the previous concept's
 * 'efectivo', or vice versa). A concept-doc `defaultPeriodicity` always wins;
 * otherwise capital behaviors start blank (the user must choose) and
 * everything else defaults to 'efectivo'. `dues` resets to 1 whenever the
 * resulting periodicity is 'efectivo' or the behavior doesn't use dues at
 * all; otherwise it's left undefined so the field renders blank rather than
 * carrying over the old count.
 */
export const deriveConceptDefaults = (meta) => {
	const behavior = (meta && meta.behavior) || 'periodic';
	const periodicity = (meta && meta.defaultPeriodicity)
		|| (behaviorNeedsCapital(behavior) ? '' : 'efectivo');
	const dues = (periodicity === 'efectivo' || !behaviorNeedsDues(behavior)) ? 1 : undefined;
	return { periodicity, dues };
};

/**
 * Per-behavior required-field validation. Returns the missing field display
 * names in the same order the legacy per-concept validators produced them so
 * the toast messages stay identical. At least as strict as the backend
 * validateEntityAgainstConcept so users get friendly errors first.
 */
export const validateByMeta = (meta, state) => {
	const behavior = (meta && meta.behavior) || 'periodic';
	const { periodicity, capital, interest, dues, amount, contract, assetSale } = state;
	const missing = [];
	const isEfectivo = periodicity === 'efectivo';
	const interestMissing = isBlank(interest) || Number(interest) < 0;

	if (behavior === 'periodic') {
		if (meta && meta.requiresContract && !contract) missing.push('Contrato');
		if (meta && meta.treatAsExpense) {
			if (!periodicity) missing.push('Periodicidad');
			if (isBlank(amount)) missing.push('Monto');
		}
	} else if (behavior === 'deposit') {
		if (!contract) missing.push('Contrato');
		if (!dues && depositNeedsDues(behavior, periodicity)) missing.push('Cuotas');
	} else if (behavior === 'account-delivery') {
		if (!capital) missing.push('Capital');
		if (!dues) missing.push('Cuotas');
	} else if (behaviorNeedsCapital(behavior)) {
		if (meta && meta.requiresAssetSale && !assetSale) missing.push('Venta de activo fijo');
		if (!capital) missing.push('Capital');
		if (!isEfectivo && interestMissing) missing.push('Interés');
		if (!periodicity) missing.push('Periodicidad');
		if (behaviorNeedsDues(behavior) && !dues && !isEfectivo) missing.push('Cuotas');
	}

	return missing;
};

/**
 * Apply the per-behavior request payload quirks the editors historically
 * hardcoded per slug. Mutates and returns requestData.
 * options.entityType: 'debt' (default) | 'credit' — the two editors send
 * slightly different payloads for the same behavior (endDate wrapping,
 * efectivo handling), preserved bug-for-bug.
 */
export const buildConceptData = (meta, state, requestData, options = {}) => {
	const entityType = options.entityType || 'debt';
	const behavior = (meta && meta.behavior) || 'periodic';
	const { contract, assetSale, endDate, interest, dues, capital, periodicity } = state;
	const isEfectivo = periodicity === 'efectivo';

	if (entityType === 'credit') {
		if (behavior === 'periodic' && meta && meta.requiresContract) {
			if (contract && contract.length > 0) {
				requestData.contract = contract[0];
				requestData.endDate = endDate;
				requestData.periodicity = '';
				requestData.interest = '';
				requestData.dues = '';
				requestData.capital = '';
			}
		} else if (behavior === 'periodic' || behavior === 'deposit' || behavior === 'one-off') {
			// Custom/simple concepts: single or periodic fixed amount.
			requestData.periodicity = periodicity || 'efectivo';
			requestData.dues = 1;
		} else if (behavior === 'amortizable' || behavior === 'installment-sale') {
			requestData.periodicity = periodicity;
			if (!isEfectivo) requestData.interest = interest;
			requestData.capital = capital;
			requestData.dues = isEfectivo ? 1 : dues;
		} else if (behavior === 'interest-only') {
			requestData.periodicity = periodicity;
			if (!isEfectivo) {
				requestData.interest = interest;
				requestData.endDate = endDate;
			}
			requestData.capital = capital;
			if (isEfectivo) requestData.dues = 1;
		} else if (behavior === 'account-delivery') {
			requestData.periodicity = periodicity;
			requestData.capital = capital;
			requestData.dues = isEfectivo ? 1 : dues;
			requestData.interest = 0; // No interest for entrega-cuenta
		}
		return requestData;
	}

	// entityType === 'debt'
	if (behavior === 'periodic' && meta && meta.requiresContract && !meta.treatAsExpense) {
		if (contract && contract.length > 0) {
			requestData.contract = contract[0];
			requestData.endDate = moment.utc(endDate);
			requestData.interest = '';
			requestData.dues = '';
			requestData.capital = '';
		}
	} else if (behavior === 'periodic' && meta && meta.treatAsExpense) {
		requestData.contract = contract[0];
		requestData.interest = '';
		requestData.capital = '';
		requestData.dues = '';
		requestData.guaranteedAmount = 0;
		if (periodicity !== 'eventual') {
			requestData.endDate = moment.utc(endDate);
		}
	} else if (behavior === 'deposit') {
		if (contract && contract.length > 0) {
			requestData.contract = contract[0];
			requestData.dues = isEfectivo ? 1 : dues;
			requestData.interest = '';
			requestData.capital = '';
			requestData.periodicity = periodicity;
			requestData.guaranteedAmount = 0;
		}
	} else if (behavior === 'amortizable' || behavior === 'installment-sale') {
		if (isEfectivo) {
			requestData.interest = '';
			requestData.dues = 1;
		} else {
			requestData.interest = interest;
			requestData.dues = dues;
		}
		requestData.capital = capital;
		requestData.guaranteedAmount = 0;
		if (meta && meta.requiresAssetSale) {
			requestData.assetSale = assetSale[0];
		}
	} else if (behavior === 'interest-only') {
		requestData.interest = isEfectivo ? '' : interest;
		requestData.capital = capital;
		requestData.endDate = moment.utc(endDate);
		requestData.guaranteedAmount = 0;
	} else if (behavior === 'account-delivery') {
		requestData.periodicity = periodicity;
		requestData.capital = capital;
		requestData.dues = isEfectivo ? 1 : dues;
		requestData.interest = 0;
		requestData.guaranteedAmount = 0;
	}
	return requestData;
};
